import React from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import {
  createManagerAction,
  ManagerActionType,
} from '@src/admin/reducers/manager/managerActions'
import { Button } from '@mui/material'
import { useGlobalSnackbar, useLoading } from '@src/hooks'
import ContentHeader from '@components/ContentHeader'
import DataTable from '@components/DataTable'
import { ControlledTextInput } from '@components/hookForm'
import { ManagerDetail, ManagerKeys } from '@admin/models/manager'
import {
  CHARACTER_LIMIT,
  emailPattern,
  INVALID_FORMAT,
  REQUIRED,
} from '@src/utils/validation'
import commonMessages from '@common/messages'
import messages from '@admin/utils/messages'
import { ReactComponent as IconAdd } from '@src/assets/icons/iconAdd.svg'
import { DETAIL_BREADCRUMBS } from './utils'
import { adminPath, toAdminRoute } from '@src/common/appConfig'
import { ErrorResponse } from '@src/utils/type'
import Snackbar, { SnackbarState } from '@src/components/Snackbar'

export default function ManagerCreate() {
  const dispatch = useDispatch()
  const { formatMessage: f } = useIntl()
  const navigate = useNavigate()
  const { control, handleSubmit } = useForm<ManagerDetail>({})
  const { status, setSnackbar, clearSnackbar } = useGlobalSnackbar({
    page: 'manager',
  })
  const { openSpinner } = useLoading({
    endActions: {
      success: [ManagerActionType.CREATE_MANAGER_SUCCESS],
      error: [ManagerActionType.CREATE_MANAGER_ERROR],
    },
    cbk: {
      success: () => {
        setSnackbar({
          state: SnackbarState.Success,
          message: f(commonMessages.createSuccess),
          notKeepData: false,
        })
        navigate(toAdminRoute(adminPath.manager.list))
      },
      error: ({ error }: { error: ErrorResponse }) => {
        setSnackbar({
          state: SnackbarState.Error,
          message: error.message,
          notKeepData: true,
        })
        document.documentElement.scrollTop = 0
      },
    },
  })

  const onSubmit = (data: ManagerDetail) => {
    openSpinner()
    dispatch(createManagerAction(data))
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ContentHeader
        title={f(messages.managerCreate)}
        breadcrumbs={DETAIL_BREADCRUMBS.concat({
          title: messages.managerCreate,
        })}
        buttonList={[
          <Button variant="contained" type="submit" startIcon={<IconAdd />}>
            {f(commonMessages.create)}
          </Button>,
        ]}
      />
      {status && (
        <Snackbar
          severity={status.state}
          message={status.message!}
          onClose={clearSnackbar}
        />
      )}
      <DataTable
        title={f(commonMessages.basicInfo)}
        dataSets={[
          {
            label: f(commonMessages.name),
            content: (
              <ControlledTextInput
                name={ManagerKeys.Name}
                control={control}
                required={REQUIRED}
                rules={{
                  maxLength: {
                    value: CHARACTER_LIMIT,
                    message: INVALID_FORMAT,
                  },
                }}
              />
            ),
          },
          {
            label: f(commonMessages.email),
            content: (
              <ControlledTextInput
                name={ManagerKeys.Email}
                control={control}
                required={REQUIRED}
                rules={{
                  pattern: emailPattern,
                }}
              />
            ),
          },
          {
            label: f(commonMessages.status),
          },
        ]}
      />
    </form>
  )
}
